import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import ProtectedRoute from "../core/components/ProtectedRoute";
import {
  EXTENSION_OPENED_QUERY_PARAM,
  EXTENSION_OPENED_QUERY_VALUE,
  EXTENSION_OPENED_SESSION_STORAGE_KEY,
} from "../core/utils/extensionSession";
import BusinessDigestWizard from "./components/BusinessDigestWizard";
import AttioConnectPanel from "./components/AttioConnectPanel";
import SalesforceConnectPanel from "./components/SalesforceConnectPanel";
import BusinessModeLayout from "./components/BusinessModeLayout";
import BusinessWorkflowSettings from "./components/BusinessWorkflowSettings";
import ActionItemsSettingsPage from "./components/ActionItemsSettingsPage";
import GlobalActionItemsPage from "./components/GlobalActionItemsPage";
import BusinessOnboardingPage from "./components/BusinessOnboardingPage";
import DesktopUpdateModal from "./components/DesktopUpdateModal";
import ElectronWindowDragStrip from "./components/ElectronWindowDragStrip";
import BusinessOAuthLegacyCallbackHandler from "./components/shared/BusinessOAuthLegacyCallbackHandler";
import {
  BUSINESS_OAUTH_ROUTES_BY_STATE,
  BUSINESS_OAUTH_STATES,
  parseBusinessOAuthLegacyCallback,
  resolveBusinessOauthState,
} from "./components/shared/businessDigestSetup";
import { getPerformerOAuthRedirectPath } from "./components/performerGmailOAuth";
import {
  clearActionItemsOnboardingLocalState,
  clearManualActionItemsModePreferred,
} from "./components/actionItemHelpers";
import { resetPerformerOnboardingLocalState } from "./components/shared/performerAssistGate";
import {
  DesktopOAuthLoopbackBounce,
  useDesktopOAuthLoopback,
} from "./components/shared/useDesktopOAuthLoopback";
import PWAInstallBanner from "./PWAInstallBanner";
import { PWAInstallProvider } from "./PWAInstallContext";
import MicrosoftWaitlist from "./MicrosoftWaitlist";
import taskMantisLockupStacked from "./task-mantis-lockup-stacked.svg";
import taskMantisLockupDark from "./task-mantis-lockup-dark-bg.svg";

const zoomHttpsWorkaroundEnabled =
  String(process.env.REACT_APP_ZOOM_HTTPS_WORKAROUND || "false")
    .trim()
    .toLowerCase() === "true";

/**
 * Return whether an OAuth state belongs to Task Mantis.
 *
 * @param {string|null} state Raw OAuth state.
 * @returns {boolean} True when dataeng must not exchange this callback.
 * @example
 * isMantisOAuthState("business_gmail");
 * // true
 */
export const isMantisOAuthState = (state) =>
  BUSINESS_OAUTH_STATES.has(resolveBusinessOauthState(state));

/**
 * Preserve the configured `/sources/add` callback behavior while composing
 * the dataeng form without importing dataeng from the mantis tree.
 *
 * @param {{AddSourceComponent: React.ComponentType}} props Composed form component.
 * @returns {React.ReactElement} Redirect handler or source form.
 * @example
 * <MantisAwareAddSourceRoute AddSourceComponent={AddSource} />
 */
export const MantisAwareAddSourceRoute = ({ AddSourceComponent }) => {
  const location = useLocation();
  const performerRedirect = getPerformerOAuthRedirectPath(location.search);
  if (performerRedirect) {
    return <Navigate to={performerRedirect} replace />;
  }
  const oauthCancelCallback = parseBusinessOAuthLegacyCallback(location.search);
  if (oauthCancelCallback) {
    return <BusinessOAuthLegacyCallbackHandler callback={oauthCancelCallback} />;
  }
  return <AddSourceComponent isReservedOAuthState={isMantisOAuthState} />;
};

export const MantisAuthenticatedRedirect = ({ homePath }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const oauthCancelCallback = parseBusinessOAuthLegacyCallback(location.search);
  if (oauthCancelCallback) {
    return <BusinessOAuthLegacyCallbackHandler callback={oauthCancelCallback} />;
  }
  if (params.get("code")) {
    const state = params.get("state");
    const normalizedState = String(state || "").trim().toLowerCase();
    const host = String(window.location.hostname || "").trim().toLowerCase();
    const isLocalHost = host === "localhost" || host === "127.0.0.1";
    const isZoomOauthState = normalizedState.includes("zoom");
    const alreadyBounced = params.get("oauth_host_bounce") === "1";

    if (
      zoomHttpsWorkaroundEnabled &&
      !isLocalHost &&
      isZoomOauthState &&
      !alreadyBounced
    ) {
      const bounceParams = new URLSearchParams(location.search);
      bounceParams.set("oauth_host_bounce", "1");
      window.location.replace(
        `http://localhost:3000${location.pathname}?${bounceParams.toString()}`
      );
      return null;
    }

    const performerRedirect = getPerformerOAuthRedirectPath(location.search);
    if (performerRedirect) {
      return <Navigate to={performerRedirect} replace />;
    }
    const businessRoute =
      BUSINESS_OAUTH_ROUTES_BY_STATE[resolveBusinessOauthState(state)];
    if (businessRoute) {
      const returnParams = new URLSearchParams(location.search);
      const extPersisted =
        sessionStorage.getItem(EXTENSION_OPENED_SESSION_STORAGE_KEY) ===
        EXTENSION_OPENED_QUERY_VALUE;
      if (
        extPersisted &&
        returnParams.get(EXTENSION_OPENED_QUERY_PARAM) !==
          EXTENSION_OPENED_QUERY_VALUE
      ) {
        returnParams.set(
          EXTENSION_OPENED_QUERY_PARAM,
          EXTENSION_OPENED_QUERY_VALUE
        );
      }
      const query = returnParams.toString();
      return (
        <Navigate
          to={`${businessRoute}${query ? `?${query}` : ""}`}
          replace
        />
      );
    }
  }
  return <Navigate to={homePath} replace />;
};

const OAuthPopupHostBounce = () => {
  const location = useLocation();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const state = String(params.get("state") || "").trim().toLowerCase();
    const alreadyBounced = params.get("oauth_host_bounce") === "1";
    const host = String(window.location.hostname || "").trim().toLowerCase();
    const isLocalHost = host === "localhost" || host === "127.0.0.1";
    const isPopupState =
      state.endsWith("_popup") ||
      state.endsWith("_popup_desktop") ||
      state.endsWith("_popup_desktop2") ||
      state.includes("_popup.") ||
      state.includes("_popup_desktop.") ||
      state.includes("_popup_desktop2.");

    if (
      !zoomHttpsWorkaroundEnabled ||
      !code ||
      !isPopupState ||
      isLocalHost ||
      alreadyBounced
    ) {
      return;
    }
    const bounceParams = new URLSearchParams(location.search);
    bounceParams.set("oauth_host_bounce", "1");
    window.location.replace(
      `http://localhost:3000${location.pathname}?${bounceParams.toString()}`
    );
  }, [location.pathname, location.search]);

  return null;
};

/**
 * Render authenticated Task Mantis routes.
 *
 * @param {{authenticatedHomePath?: string}} props Authenticated fallback path.
 * @returns {React.ReactElement} Mantis onboarding and business route tree.
 * @example
 * <MantisProduct />
 */
export const MantisProduct = ({
  authenticatedHomePath = "/business/action-items",
}) => (
  <Routes>
    <Route
      path="/onboarding"
      element={<ProtectedRoute><BusinessOnboardingPage /></ProtectedRoute>}
    />
    <Route
      path="/business/*"
      element={<ProtectedRoute><BusinessModeLayout /></ProtectedRoute>}
    >
      <Route path="action-items" element={<GlobalActionItemsPage />} />
      <Route path="settings" element={<ActionItemsSettingsPage />} />
      <Route path="profile" element={<Navigate to="/business/settings?settingsTab=general" replace />} />
      <Route path="workflows" element={<Navigate to="/business/settings?settingsTab=connect-apps" replace />} />
      <Route path="workflows/new" element={<Navigate to="/business/settings?settingsTab=connect-apps" replace />} />
      <Route path="workflows/new/email-digest" element={<BusinessDigestWizard sourceType="gmail" />} />
      <Route path="workflows/new/zoom-digest" element={<BusinessDigestWizard sourceType="zoom" />} />
      <Route path="workflows/new/google-meet-digest" element={<BusinessDigestWizard sourceType="google_meet" />} />
      <Route path="workflows/new/gong-digest" element={<BusinessDigestWizard sourceType="gong" />} />
      <Route path="workflows/new/granola-digest" element={<BusinessDigestWizard sourceType="granola" />} />
      <Route path="workflows/new/notion-digest" element={<BusinessDigestWizard sourceType="notion" />} />
      <Route path="workflows/new/calendar-digest" element={<BusinessDigestWizard sourceType="google_calendar" />} />
      <Route path="workflows/new/slack-digest" element={<BusinessDigestWizard sourceType="slack" />} />
      <Route path="workflows/new/attio" element={<AttioConnectPanel />} />
      <Route path="workflows/new/salesforce" element={<SalesforceConnectPanel />} />
      <Route path="workflows/:workflowId" element={<BusinessWorkflowSettings />} />
      <Route path="*" element={<Navigate to="/business/action-items" replace />} />
    </Route>
    <Route
      path="*"
      element={
        <MantisAuthenticatedRedirect homePath={authenticatedHomePath} />
      }
    />
  </Routes>
);

/**
 * Add Task Mantis PWA and Electron behavior around the shared route shell.
 *
 * @param {{children: React.ReactNode}} props Shared app routes.
 * @returns {React.ReactElement} Wrapped app tree.
 * @example
 * <MantisChrome><main /></MantisChrome>
 */
export const MantisChrome = ({ children }) => {
  useDesktopOAuthLoopback();
  React.useEffect(() => {
    if (
      !("serviceWorker" in navigator) ||
      window.starmeshDesktop?.isElectron
    ) {
      return undefined;
    }
    const registerServiceWorker = () => {
      void navigator.serviceWorker.register("/sw.js");
    };
    window.addEventListener("load", registerServiceWorker);
    return () => {
      window.removeEventListener("load", registerServiceWorker);
    };
  }, []);

  return (
    <>
      <DesktopOAuthLoopbackBounce />
      <PWAInstallProvider>
        {/* First in the DOM so every later no-drag control subtracts from it. */}
        <ElectronWindowDragStrip />
        <OAuthPopupHostBounce />
        {children}
        <PWAInstallBanner />
        <DesktopUpdateModal />
      </PWAInstallProvider>
    </>
  );
};

export const mantisAuthPresentation = {
  brand: { imageSrc: taskMantisLockupStacked, alt: "Task Mantis" },
  headline: (
    <>
      Every call ends with follow-ups.
      <br />
      Task Mantis gets them done.
    </>
  ),
  Waitlist: MicrosoftWaitlist,
};

export const mantisNavBrand = {
  imageSrc: taskMantisLockupDark,
  alt: "Task Mantis Logo",
};

export const mantisAuthLifecycle = {
  onFreshLogin: clearManualActionItemsModePreferred,
  onSessionCleared: () => {
    clearActionItemsOnboardingLocalState();
    resetPerformerOnboardingLocalState();
  },
  resolvePostLoginRedirect: getPerformerOAuthRedirectPath,
};
